/**
 * GoodGuardian — Expense Analytics
 *
 * Visualizes the shared expense ledger with 4 charts:
 *   1. Spending by category (horizontal bars)
 *   2. Monthly trend (SVG bar chart)
 *   3. Status breakdown (SVG donut)
 *   4. Balance summary (you owe / they owe)
 *
 * Read-only — fetches the same expense data as ExpenseLog.
 * No charting library required — pure SVG + CSS.
 */
import { useState, useEffect, useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import { data as api } from '@/utils/auth'

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'medical',         label: 'Medical',       emoji: '🏥', color: '#3b82f6' },
  { id: 'education',       label: 'Education',     emoji: '📚', color: '#6366f1' },
  { id: 'extracurricular', label: 'Activities',    emoji: '⚽', color: '#22c55e' },
  { id: 'clothing',        label: 'Clothing',      emoji: '👕', color: '#ec4899' },
  { id: 'food',            label: 'Food',          emoji: '🍽️', color: '#f59e0b' },
  { id: 'childcare',       label: 'Childcare',     emoji: '🧸', color: '#8b5cf6' },
  { id: 'transport',       label: 'Transport',     emoji: '🚗', color: '#06b6d4' },
  { id: 'other',           label: 'Other',         emoji: '📎', color: '#94a3b8' },
]
const CAT_MAP   = Object.fromEntries(CATEGORIES.map(c => [c.id, c]))

const STATUSES = [
  { id: 'pending',  label: 'Pending',  color: '#f59e0b' },
  { id: 'approved', label: 'Approved', color: '#22c55e' },
  { id: 'paid',     label: 'Paid',     color: '#2E75B6' },
  { id: 'rejected', label: 'Declined', color: '#ef4444' },
]

const BRAND = '#2E75B6'

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0)
}

// ── Data derivation ───────────────────────────────────────────────────────────
function deriveData(expenses, userId) {
  // ── Category totals ──
  const catTotals = {}
  for (const e of expenses) {
    catTotals[e.category] = (catTotals[e.category] || 0) + (e.total_amount || 0)
  }
  const byCategory = CATEGORIES
    .map(c => ({ ...c, total: catTotals[c.id] || 0 }))
    .filter(c => c.total > 0)
    .sort((a, b) => b.total - a.total)

  // ── Monthly trend (last 12 months) ──
  const now       = new Date()
  const months    = []
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({
      key:   `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short' }),
      year:  d.getFullYear(),
      isCurrent: i === 0,
      total: 0,
    })
  }
  const monthMap = Object.fromEntries(months.map(m => [m.key, m]))
  for (const e of expenses) {
    const dateStr = e.purchase_date || e.created_at?.slice(0, 10)
    if (!dateStr) continue
    const key = dateStr.slice(0, 7)
    if (monthMap[key]) monthMap[key].total += e.total_amount || 0
  }
  // Trim leading empty months to last 6 with data, min 3 shown
  const nonEmpty = months.filter(m => m.total > 0)
  const monthsToShow = nonEmpty.length >= 3
    ? months.slice(Math.max(0, months.length - Math.max(6, nonEmpty.length)))
    : months.slice(-6)

  // ── Status breakdown ──
  const statusTotals = {}
  const statusCounts = {}
  for (const e of expenses) {
    statusTotals[e.status] = (statusTotals[e.status] || 0) + (e.total_amount || 0)
    statusCounts[e.status] = (statusCounts[e.status] || 0) + 1
  }
  const byStatus = STATUSES
    .map(s => ({ ...s, total: statusTotals[s.id] || 0, count: statusCounts[s.id] || 0 }))
    .filter(s => s.count > 0)

  // ── Balance ──
  const balance = expenses
    .filter(e => e.status !== 'rejected')
    .reduce((acc, e) => {
      const isMyExpense = e.submitted_by === userId
      if (isMyExpense) acc.theyOwe += (e.total_amount || 0) * (100 - (e.payer_pct || 50)) / 100
      else             acc.youOwe  += (e.total_amount || 0) * (e.payer_pct || 50) / 100
      return acc
    }, { youOwe: 0, theyOwe: 0 })
  const balanceNet  = balance.theyOwe - balance.youOwe  // positive = net owed to me

  // ── Summary ──
  const totalSpend = expenses.reduce((s, e) => s + (e.total_amount || 0), 0)
  const avgExpense = expenses.length > 0 ? totalSpend / expenses.length : 0
  const paidCount  = expenses.filter(e => e.status === 'paid').length

  return { byCategory, monthsToShow, byStatus, balance, balanceNet, totalSpend, avgExpense, paidCount }
}

// ── Chart: Horizontal Category Bars ───────────────────────────────────────────
function CategoryBars({ data }) {
  if (data.length === 0) return <EmptyChart label="No expenses logged yet." />
  const max = data[0].total
  return (
    <div className="space-y-3">
      {data.map(cat => {
        const pct = max > 0 ? (cat.total / max) * 100 : 0
        return (
          <div key={cat.id}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                <span>{cat.emoji}</span>{cat.label}
              </span>
              <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{fmtMoney(cat.total)}</span>
            </div>
            <div className="h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, backgroundColor: cat.color }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Chart: Monthly Trend SVG Bar Chart ────────────────────────────────────────
const BAR_H    = 120   // chart drawing area height (px, SVG units)
const BAR_PAD  = 4     // padding at top so tallest bar isn't flush
const LABEL_H  = 18   // space for month labels below bars

function MonthlyBars({ months }) {
  const max    = Math.max(...months.map(m => m.total), 1)
  const n      = months.length
  const svgW   = 100
  const svgH   = BAR_H + LABEL_H
  const barW   = (svgW / n) * 0.55
  const gap    = (svgW / n) * 0.45

  return (
    <div>
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="w-full overflow-visible"
        style={{ height: 140 }}
        aria-label="Monthly spending trend"
      >
        {months.map((m, i) => {
          const barH   = ((m.total / max) * (BAR_H - BAR_PAD))
          const x      = i * (svgW / n) + gap / 2
          const y      = BAR_H - barH
          const fill   = m.isCurrent ? BRAND : '#bfdbfe'
          const amount = m.total > 0 ? fmtMoney(m.total) : ''

          return (
            <g key={m.key}>
              {m.total > 0 && (
                <rect x={x} y={y} width={barW} height={Math.max(barH, 0)} rx="1.5" fill={fill} />
              )}
              {m.total === 0 && (
                <rect x={x} y={BAR_H - 2} width={barW} height={2} rx="1" fill="#e2e8f0" />
              )}
              {m.total > 0 && barH > 14 && (
                <text
                  x={x + barW / 2}
                  y={y + 8}
                  textAnchor="middle"
                  fontSize="3.8"
                  fill={m.isCurrent ? '#fff' : '#1e40af'}
                  fontWeight="bold"
                >
                  {fmtMoney(m.total).replace('$', '')}
                </text>
              )}
              <text
                x={x + barW / 2}
                y={svgH - 3}
                textAnchor="middle"
                fontSize="4"
                fill={m.isCurrent ? BRAND : '#94a3b8'}
                fontWeight={m.isCurrent ? 'bold' : 'normal'}
              >
                {m.label}
              </text>
            </g>
          )
        })}
      </svg>
      <div className="flex items-center gap-3 mt-1 justify-end">
        <span className="flex items-center gap-1 text-[10px] text-slate-400">
          <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: BRAND }} /> This month
        </span>
        <span className="flex items-center gap-1 text-[10px] text-slate-400">
          <span className="inline-block w-3 h-2 rounded-sm bg-blue-200" /> Prior months
        </span>
      </div>
    </div>
  )
}

// ── Chart: Status Donut ───────────────────────────────────────────────────────
const DONUT_R  = 36
const DONUT_C  = 2 * Math.PI * DONUT_R

function StatusDonut({ segments, total }) {
  if (segments.length === 0) return <EmptyChart label="No expenses logged yet." />

  let accumulated = 0
  const totalCount = segments.reduce((s, seg) => s + seg.count, 0)

  return (
    <div className="flex items-center gap-4">
      {/* SVG donut */}
      <div className="shrink-0" style={{ width: 100, height: 100 }}>
        <svg viewBox="0 0 100 100" width="100" height="100">
          {/* Background circle */}
          <circle cx="50" cy="50" r={DONUT_R} fill="none" stroke="#f1f5f9" strokeWidth="18" />

          {segments.map((seg, i) => {
            const segLen    = (seg.count / totalCount) * DONUT_C
            const startAngle = (accumulated / totalCount) * 360
            accumulated    += seg.count
            return (
              <circle
                key={i}
                cx="50" cy="50" r={DONUT_R}
                fill="none"
                stroke={seg.color}
                strokeWidth="18"
                strokeDasharray={`${segLen} ${DONUT_C}`}
                strokeDashoffset={DONUT_C / 4}
                transform={`rotate(${startAngle} 50 50)`}
              />
            )
          })}

          {/* Center label */}
          <text x="50" y="47" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#1e293b">
            {totalCount}
          </text>
          <text x="50" y="57" textAnchor="middle" fontSize="5.5" fill="#94a3b8">
            expenses
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="flex-1 space-y-2">
        {segments.map(seg => (
          <div key={seg.id} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
              <span className="text-xs text-slate-600 dark:text-slate-300">{seg.label}</span>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{seg.count}</span>
              <span className="text-[10px] text-slate-400 ml-1">({fmtMoney(seg.total)})</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Chart: Balance Summary ────────────────────────────────────────────────────
function BalanceSummary({ balance, balanceNet }) {
  const { youOwe, theyOwe } = balance
  const maxSide = Math.max(youOwe, theyOwe, 0.01)
  const youPct  = Math.round((youOwe  / maxSide) * 100)
  const theyPct = Math.round((theyOwe / maxSide) * 100)
  const settled = youOwe < 0.01 && theyOwe < 0.01

  if (settled) {
    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <span className="text-3xl">🎉</span>
        <p className="text-sm font-bold text-green-600">All settled up!</p>
        <p className="text-xs text-slate-400">No outstanding balances on active expenses.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Bar comparison */}
      <div className="space-y-3">
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-xs font-semibold text-purple-600">You owe</span>
            <span className="text-sm font-bold text-purple-700">{fmtMoney(youOwe)}</span>
          </div>
          <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-purple-400 transition-all duration-700"
              style={{ width: `${youPct}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-xs font-semibold text-orange-500">Co-Parent owes</span>
            <span className="text-sm font-bold text-orange-600">{fmtMoney(theyOwe)}</span>
          </div>
          <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-orange-400 transition-all duration-700"
              style={{ width: `${theyPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Net result */}
      <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${
        balanceNet > 0
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          : 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
      }`}>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Net balance</p>
          <p className={`text-sm font-bold mt-0.5 ${balanceNet > 0 ? 'text-green-700 dark:text-green-400' : 'text-purple-700 dark:text-purple-400'}`}>
            {balanceNet > 0
              ? `Co-Parent owes you ${fmtMoney(Math.abs(balanceNet))}`
              : `You owe ${fmtMoney(Math.abs(balanceNet))}`}
          </p>
        </div>
        <span className="text-2xl">{balanceNet > 0 ? '💰' : '📤'}</span>
      </div>
    </div>
  )
}

// ── Shared empty state ────────────────────────────────────────────────────────
function EmptyChart({ label }) {
  return (
    <div className="py-6 text-center">
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  )
}

// ── Chart card wrapper ────────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children }) {
  return (
    <div className="card p-4">
      <p className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-0.5">{title}</p>
      {subtitle && <p className="text-[10px] text-slate-400 mb-3">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      {children}
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function ExpenseAnalytics() {
  const { user } = useApp()
  const [expenses, setExpenses] = useState([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    api.getExpenses()
      .then(d => setExpenses(d.expenses || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const { byCategory, monthsToShow, byStatus, balance, balanceNet, totalSpend, avgExpense, paidCount } =
    useMemo(() => deriveData(expenses, user?.id), [expenses, user?.id])

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <AppHeader title="Expense Analytics" showBack />

      <div className="screen no-scrollbar px-4 py-4 space-y-4">

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Total logged',  value: fmtMoney(totalSpend), color: 'text-brand-700' },
            { label: 'Avg expense',   value: fmtMoney(avgExpense),  color: 'text-slate-700 dark:text-slate-300' },
            { label: 'Paid off',      value: paidCount,             color: 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="card py-3 text-center">
              <p className={`text-base font-bold leading-tight ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="card py-12 text-center">
            <p className="text-sm text-slate-400">Loading analytics…</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="card py-12 text-center">
            <p className="text-3xl mb-2">📊</p>
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No expense data yet</p>
            <p className="text-xs text-slate-400 mt-1">Start logging shared expenses to see analytics here.</p>
          </div>
        ) : (
          <>
            {/* Chart 1: Monthly Trend */}
            <ChartCard
              title="Monthly Spending"
              subtitle={`Last ${monthsToShow.length} months · ${expenses.length} total expenses`}
            >
              <MonthlyBars months={monthsToShow} />
            </ChartCard>

            {/* Chart 2: Category Breakdown */}
            <ChartCard
              title="Spending by Category"
              subtitle={`${byCategory.length} categor${byCategory.length === 1 ? 'y' : 'ies'} · ${fmtMoney(totalSpend)} total`}
            >
              <CategoryBars data={byCategory} />
            </ChartCard>

            {/* Charts 3 + 4: side by side on wider screens, stacked on mobile */}
            <div className="grid grid-cols-1 gap-4">
              <ChartCard
                title="Status Breakdown"
                subtitle="All expenses by approval status"
              >
                <StatusDonut segments={byStatus} total={expenses.length} />
              </ChartCard>

              <ChartCard
                title="Balance Summary"
                subtitle="Outstanding amounts across active expenses"
              >
                <BalanceSummary balance={balance} balanceNet={balanceNet} />
              </ChartCard>
            </div>
          </>
        )}

        <div className="pb-2" />
      </div>
    </div>
  )
}
